import type { IconName } from "../components/Icon";

/**
 * Personlig dashbord-oppsett. Lagres i localStorage per bruker nå; byttes til
 * en per-bruker kalk-tabell når Entra SSO er på plass (samme funksjonssignatur
 * — last/lagre tar en uid, så byttet blir lokalt her).
 */
export type WidgetType =
  | "mineOppgaver"
  | "nyesteSalg"
  | "mineProsjekter"
  | "sisteKalkyler"
  | "prosjektKpi";

export type WidgetConfig = { projektId?: number };
export type WidgetInstance = {
  id: string;
  type: WidgetType;
  cols: number;
  config?: WidgetConfig;
};
export type DashboardLayout = { versjon: number; widgets: WidgetInstance[] };

// 2-kolonners rutenett: en widget er enten halv (1 = to i bredden) eller full (2).
export const MAX_COLS = 2;

// Økes når standardoppsettet/bredde-modellen endres, så gamle lagrede oppsett
// (f.eks. fra 4-kolonners-tiden) forkastes i stedet for å klampes til noe rart.
export const LAYOUT_VERSJON = 2;

export const WIDGET_KATALOG: Record<
  WidgetType,
  { navn: string; beskrivelse: string; ikon: IconName; standardCols: number }
> = {
  mineOppgaver: {
    navn: "Mine oppgaver",
    beskrivelse: "Åpne oppgaver gitt til deg",
    ikon: "checkCircle",
    standardCols: 1,
  },
  nyesteSalg: {
    navn: "Nyeste salg",
    beskrivelse: "Siste solgte enheter",
    ikon: "trendUp",
    standardCols: 1,
  },
  mineProsjekter: {
    navn: "Mine prosjekter",
    beskrivelse: "Prosjekter du er leder for",
    ikon: "buildings",
    standardCols: 1,
  },
  sisteKalkyler: {
    navn: "Siste kalkyler",
    beskrivelse: "Nylig endrede kalkyler",
    ikon: "target",
    standardCols: 1,
  },
  prosjektKpi: {
    navn: "Prosjekt-KPI",
    beskrivelse: "Nøkkeltall for et valgt prosjekt",
    ikon: "activity",
    standardCols: 2,
  },
};

export const WIDGET_TYPER = Object.keys(WIDGET_KATALOG) as WidgetType[];

// Ryddig 2-kolonners standardoppsett, to og to i bredden, så Hjem får plass på
// én skjerm. Prosjekt-KPI er halv bredde så den tomme tilstanden (før man
// velger prosjekt) ikke blir en stor tom flate. Velkommen-kortet er fjernet;
// hilsenen står i sidetittelen (Erik 02.11.2026).
//   Rad 1: Prosjekt-KPI (halv) | Siste kalkyler (halv)
//   Rad 2: Mine oppgaver (halv) | Nyeste salg (halv)
// Mine oppgaver tok plassen til Mine prosjekter (Erik 02.11.2026); Mine prosjekter finnes under Tilpass.
export const STANDARD_LAYOUT: DashboardLayout = {
  versjon: LAYOUT_VERSJON,
  widgets: [
    { id: "w-prosjektKpi", type: "prosjektKpi", cols: 1 },
    { id: "w-sisteKalkyler", type: "sisteKalkyler", cols: 1 },
    { id: "w-mineOppgaver", type: "mineOppgaver", cols: 1 },
    { id: "w-nyesteSalg", type: "nyesteSalg", cols: 1 },
  ],
};

const key = (uid: string) => `gb.dashboard.${uid}`;

// Signaturer for tidligere standardoppsett som skal «pensjoneres»: en nettleser
// som har frosset et av disse (uten å tilpasse noe) får det nye standard-
// oppsettet i stedet. Egendefinerte oppsett matcher ikke og beholdes.
const signatur = (l: DashboardLayout): string =>
  l.widgets.map((w) => `${w.type}:${w.cols}`).join("|");
const RETIRERTE_STANDARDER = new Set<string>([
  // Gammelt oppsett: Prosjekt-KPI i full bredde + rekkefølge før balansering.
  "velkommen:2|prosjektKpi:2|mineProsjekter:1|nyesteSalg:1|sisteKalkyler:1",
  // Samme oppsett etter at Velkommen ble fjernet (ukjente typer filtreres bort i saner)
  "prosjektKpi:2|mineProsjekter:1|nyesteSalg:1|sisteKalkyler:1",
  // Standardoppsettet før Mine oppgaver (02.11.2026)
  "prosjektKpi:1|sisteKalkyler:1|mineProsjekter:1|nyesteSalg:1",
]);

/** Fjern ukjente widget-typer og klamp bredden til 1..MAX_COLS. */
function saner(l: DashboardLayout): DashboardLayout {
  return {
    versjon: LAYOUT_VERSJON,
    widgets: (l.widgets ?? [])
      .filter((w) => w && WIDGET_KATALOG[w.type])
      .map((w) => ({
        id: w.id,
        type: w.type,
        cols: Math.min(MAX_COLS, Math.max(1, Math.round(w.cols) || WIDGET_KATALOG[w.type].standardCols)),
        ...(w.config ? { config: w.config } : {}),
      })),
  };
}

export function lastLayout(uid: string): DashboardLayout {
  try {
    const raw = localStorage.getItem(key(uid));
    if (raw) {
      const parsed = JSON.parse(raw) as DashboardLayout;
      // Forkast oppsett fra en eldre bredde-modell – bruk friskt standardoppsett.
      if (parsed && parsed.versjon === LAYOUT_VERSJON && Array.isArray(parsed.widgets)) {
        const ryddet = saner(parsed);
        // Frosset gammelt standardoppsett (aldri tilpasset) → gi nytt standard.
        if (RETIRERTE_STANDARDER.has(signatur(ryddet))) return STANDARD_LAYOUT;
        return ryddet;
      }
    }
  } catch {
    /* privat modus / blokkert lager – fall tilbake til standard */
  }
  return STANDARD_LAYOUT;
}

export function lagreLayout(uid: string, layout: DashboardLayout): void {
  try {
    localStorage.setItem(key(uid), JSON.stringify(layout));
  } catch {
    /* ignore – lagring er en bekvemmelighet, ikke kritisk */
  }
}

export function nyWidgetId(type: WidgetType): string {
  return `${type}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}
